import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import DocsAndFilesPage from '@/pages/docs/DocsAndFilesPage';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export const StackDocsPage = () => {
  const { stackId } = useParams();
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stack, setStack] = useState(null);
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);

  usePageTitle(stack?.name ? `${stack.name} Docs & Files` : 'Docs & Files');

  useEffect(() => {
    const loadStackData = async () => {
      if (!company?.id || !userProfile?.id) return;

      try {
        setLoading(true);

        // Load stack data from user_dashboard_settings
        const { data: settingsData, error: settingsError } = await supabase
          .from('user_dashboard_settings')
          .select('stacks')
          .eq('user_id', userProfile.id)
          .eq('company_id', company.id)
          .single();

        if (settingsError && settingsError.code !== 'PGRST116') {
          throw settingsError;
        }

        const stackData = settingsData?.stacks?.find(s => s.id.toString() === stackId.toString());

        if (!stackData) {
          throw new Error('Stack not found');
        }

        setStack(stackData);

        if (stackData.projectIds && stackData.projectIds.length > 0) {
          const { data: projectsData, error: projectsError } = await supabase
            .from('projects')
            .select('id, name')
            .in('id', stackData.projectIds)
            .eq('company_id', company.id)
            .eq('is_archived', false);

          if (projectsError) throw projectsError;

          setProjects(projectsData || []);
        }

      } catch (err) {
        console.error('Error loading stack data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadStackData();
  }, [stackId, userProfile?.id, company?.id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !stack) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error || 'Stack not found'}</p>
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <DocsAndFilesPage
      stackId={stackId}
      stackName={stack.name}
      stackProjects={projects}
      isStackView={true}
    />
  );
};
